"use client";

import Link from "next/link";
import { useTranslation } from "@/lib/i18n/context";

type ProductKey = "dailyRhythm" | "aiAssistant" | "integrations" | "goals" | "journal";

/** Page hero for the /product/* pages — kicker + H1 + lead + primary CTA. */
export function ProductIntro({ page }: { page: ProductKey }) {
  const { t, lang } = useTranslation();
  const p = t.landing.cover.productPages[page];
  const cta = t.landing.cover.hero.ctaPrimary;

  return (
    <section className="relative max-w-[860px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-4 text-center z-[2]">
      <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {lang === "en" ? "// PRODUCT" : "// PRODUKT"}
      </div>
      <h1
        className="font-display text-[clamp(36px,5vw,60px)] leading-[1.04] tracking-[-0.02em] text-balance"
        style={{ fontWeight: 380 }}
      >
        {p.h2}
      </h1>
      <p className="mt-6 text-[18px] md:text-[20px] leading-[1.6] text-[var(--ink-soft)] max-w-[640px] mx-auto text-pretty">
        {p.lead}
      </p>
      <Link
        href="/register"
        className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[var(--terracotta)] text-[var(--terracotta-soft)] font-medium text-[15px] transition hover:-translate-y-px hover:shadow-[0_8px_20px_rgba(184,83,26,0.28)]"
      >
        {cta}
      </Link>
    </section>
  );
}

/** Closing CTA band for the /product/* pages. */
export function ProductCta() {
  const { t } = useTranslation();
  const cta = t.landing.cover.hero.ctaPrimary;
  const line = t.landing.cover.productPages.ctaLine;

  return (
    <section className="relative text-center max-w-[820px] mx-auto px-6 md:px-12 py-24 md:py-[110px] border-t border-[var(--hair)] mt-10 z-[2]">
      <h2
        className="font-display text-[clamp(32px,4vw,52px)] leading-[1.05] tracking-[-0.02em] text-balance"
        style={{ fontWeight: 380 }}
      >
        {line}
      </h2>
      <Link
        href="/register"
        className="mt-7 inline-flex items-center gap-2 px-8 py-[18px] rounded-[14px] bg-[var(--terracotta)] text-[var(--terracotta-soft)] font-medium text-[16px] transition hover:-translate-y-px hover:shadow-[0_8px_20px_rgba(184,83,26,0.28)]"
      >
        {cta}
      </Link>
      <div className="font-mono text-xs text-[var(--ink-soft)] tracking-[0.08em] mt-4">
        {t.landing.cover.hero.trust.join(" ")}
      </div>
    </section>
  );
}
